/**
 * Profile Preferences Store
 * ─────────────────────────
 * Persists user preferences that affect coaching behavior.
 * Coach style, units, brief time, notification toggles.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ProfilePrefs {
  coachStyle: string;   // 'drill' | 'professor' | 'balanced' | 'mentor' | 'spartan'
  units: 'metric' | 'imperial';
  language: 'en' | 'fr';
  briefTime: string;    // "06:30"
  logReminderTime: string; // "19:00"
  preferredRunTime: 'morning' | 'afternoon' | 'evening';
  notifications: {
    morningBrief: boolean;
    nutritionNudges: boolean;
    postSession: boolean;
    interventions: boolean;
    logReminder: boolean;
  };

  setCoachStyle: (style: string) => void;
  setUnits: (units: 'metric' | 'imperial') => void;
  setLanguage: (lang: 'en' | 'fr') => void;
  setBriefTime: (time: string) => void;
  setLogReminderTime: (time: string) => void;
  setPreferredRunTime: (time: 'morning' | 'afternoon' | 'evening') => void;
  setNotification: (key: keyof ProfilePrefs['notifications'], value: boolean) => void;
}

export const useProfilePrefs = create<ProfilePrefs>()(
  persist(
    (set) => ({
      coachStyle: 'balanced',
      units: 'metric',
      language: 'en',
      briefTime: '06:30',
      logReminderTime: '19:00',
      preferredRunTime: 'morning',
      notifications: {
        morningBrief: true,
        nutritionNudges: true,
        postSession: true,
        interventions: true,
        logReminder: true,
      },

      setCoachStyle: (style) => set({ coachStyle: style }),
      setUnits: (units) => set({ units }),
      setLanguage: (lang) => set({ language: lang }),
      setBriefTime: (time) => set({ briefTime: time }),
      setLogReminderTime: (time) => set({ logReminderTime: time }),
      setPreferredRunTime: (time) => set({ preferredRunTime: time }),
      setNotification: (key, value) => set((state) => ({
        notifications: { ...state.notifications, [key]: value },
      })),
    }),
    {
      name: 'kader-profile-prefs',
      version: 2,
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          // Reset language to English — language switching feature removed
          return { ...persisted, language: 'en' };
        }
        return persisted as any;
      },
    }
  )
);
